import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '@/api/axiosConfig.js';
import { useAuth } from '../../state/auth/AuthContext.jsx';

function Avatar({ name, photoUrl }) {
  const initials = (name || 'Usuario')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part?.[0]?.toUpperCase())
    .join('');

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className="h-9 w-9 rounded-full object-cover ring-2 ring-[rgb(var(--ring))] ring-offset-2 ring-offset-white dark:ring-offset-[rgb(2,27,33)]"
      />
    );
  }

  return (
    <div
      aria-label={name}
      className="grid h-9 w-9 place-items-center rounded-full bg-[rgba(var(--brand),0.12)] font-semibold text-[rgb(var(--brand-700))] ring-2 ring-[rgb(var(--ring))] ring-offset-2 ring-offset-white dark:ring-offset-[rgb(2,27,33)]"
    >
      {initials}
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return 'Sin registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin registro';
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function getRoleLabel(user) {
  const principalRole = Array.isArray(user?.roles)
    ? user.roles.find((role) => role?.esPrincipal || String(role?.id) === String(user?.rolPrincipalId)) || user.roles[0]
    : null;
  return principalRole?.nombre || user?.rol || 'Sin rol';
}

function formatAverageHours(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 'Sin datos';
  return `${parsed.toLocaleString('es-AR', { maximumFractionDigits: 1 })} h/dia`;
}

export default function UserMenu() {
  const { user, logout, sucursalActiva, sucursalPrincipal, sucursalesHabilitadas, puedeCambiarSucursal, setSucursalActiva } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showSucursalPicker, setShowSucursalPicker] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    repeatPassword: '',
  });
  const [passwordFeedback, setPasswordFeedback] = useState(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const displayName = user?.nombre || user?.name || user?.username || 'Usuario';
  const email = user?.email || '';
  const roleLabel = getRoleLabel(user);
  const levelLabel = user?.nivel != null ? `Nivel ${user.nivel}` : null;
  const lastAccessLabel = formatDateTime(user?.ultimoLogin || user?.ultimo_login || user?.lastLogin);
  const averageHoursLabel = formatAverageHours(user?.promedioHorasDiarias || user?.promedio_horas_diarias);
  const activeSucursalId = String(sucursalActiva?.id || sucursalActiva?.sucursal_id || '');
  const activeSucursalName = sucursalActiva?.nombre || sucursalPrincipal?.nombre || 'Sucursal fija';

  const sucursalOptions = useMemo(() => {
    const principal = sucursalPrincipal ? [sucursalPrincipal] : [];
    const allowed = Array.isArray(sucursalesHabilitadas) ? sucursalesHabilitadas : [];
    const map = new Map();

    [...principal, ...allowed].forEach((item) => {
      const id = item?.id || item?.sucursal_id;
      if (!id) return;
      map.set(String(id), item);
    });

    return Array.from(map.values());
  }, [sucursalPrincipal, sucursalesHabilitadas]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setShowSucursalPicker(false);
        setShowPasswordForm(false);
      }
    }

    function handleEscape(event) {
      if (event.key !== 'Escape') return;

      if (showSucursalPicker) {
        setShowSucursalPicker(false);
        return;
      }

      if (showPasswordForm) {
        setShowPasswordForm(false);
        return;
      }

      setOpen(false);
      triggerRef.current?.focus({ preventScroll: true });
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showSucursalPicker, showPasswordForm]);

  const handleSucursalChange = async (nextSucursalId) => {
    if (!nextSucursalId || nextSucursalId === activeSucursalId) {
      setShowSucursalPicker(false);
      return;
    }

    const result = await Swal.fire({
      title: 'Cambiar sucursal',
      text: 'Cambiar la sucursal activa recargará la pantalla y limpiará datos dependientes. ¿Continuar?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Aceptar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    try {
      await setSucursalActiva(nextSucursalId, {
        motivo: 'Cambio desde menú de perfil',
      });
      window.location.reload();
    } catch (error) {
      window.alert(error?.response?.data?.message || error?.message || 'No se pudo cambiar la sucursal activa.');
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm((current) => ({ ...current, [field]: value }));
    setPasswordFeedback(null);
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.repeatPassword) {
      setPasswordFeedback({ type: 'error', message: 'Completá todos los campos.' });
      return;
    }

    if (passwordForm.newPassword.length < 4 || passwordForm.newPassword.length > 64) {
      setPasswordFeedback({ type: 'error', message: 'La nueva clave debe tener entre 4 y 64 caracteres.' });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.repeatPassword) {
      setPasswordFeedback({ type: 'error', message: 'La confirmación no coincide.' });
      return;
    }

    setPasswordSaving(true);
    setPasswordFeedback(null);

    try {
      const response = await api.post('/auth/cambiar-clave', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', repeatPassword: '' });
      setPasswordFeedback({ type: 'success', message: response.data?.message || 'Clave actualizada correctamente.' });
    } catch (error) {
      setPasswordFeedback({
        type: 'error',
        message: error?.response?.data?.message || error?.message || 'No se pudo cambiar la clave.',
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-xl p-1 hover:bg-[rgba(var(--subpanel),1)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
        title={displayName}
        type="button"
      >
        <Avatar name={displayName} photoUrl={user?.photoUrl} />
        <span className="hidden max-w-[140px] truncate font-medium text-[rgb(var(--fg))]/80 md:inline">{displayName}</span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-72 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),1)] p-2 text-sm shadow-xl"
        >
          <div className="px-3 py-2">
            <div className="text-sm font-semibold text-[rgb(var(--fg))]">{displayName}</div>
            {email ? <div className="text-xs text-[rgb(var(--muted))]">{email}</div> : null}
            <div className="mt-3 grid gap-2 rounded-lg bg-[rgba(var(--subpanel),1)] px-2.5 py-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--muted))]">Sucursal activa</div>
                <div className="mt-1 text-sm font-medium text-[rgb(var(--fg))]">{activeSucursalName}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 border-t border-[rgba(var(--border),0.7)] pt-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--muted))]">Rol</div>
                  <div className="mt-1 truncate text-xs font-medium text-[rgb(var(--fg))]" title={roleLabel}>
                    {roleLabel}
                  </div>
                  {levelLabel ? <div className="text-[11px] text-[rgb(var(--muted))]">{levelLabel}</div> : null}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--muted))]">Ultimo acceso</div>
                  <div className="mt-1 text-xs font-medium text-[rgb(var(--fg))]">{lastAccessLabel}</div>
                </div>
              </div>
              <div className="border-t border-[rgba(var(--border),0.7)] pt-2">
                <div className="text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--muted))]">Promedio diario</div>
                <div className="mt-1 text-sm font-medium text-[rgb(var(--fg))]">{averageHoursLabel}</div>
              </div>
            </div>
          </div>

          {puedeCambiarSucursal ? (
            <>
              <hr className="my-2 border-[rgb(var(--border))]" />
              <button
                type="button"
                onClick={() => setShowSucursalPicker((value) => !value)}
                className="w-full rounded-lg px-3 py-2 text-left hover:bg-[rgba(var(--subpanel),1)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                Cambiar sucursal
              </button>

              {showSucursalPicker ? (
                <div className="mt-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.7)] p-2">
                  <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--muted))]">
                    Sucursales habilitadas
                  </div>
                  <div className="flex flex-col gap-1">
                    {sucursalOptions.map((item) => {
                      const optionId = String(item?.id || item?.sucursal_id);
                      const isCurrent = optionId === activeSucursalId;

                      return (
                        <button
                          key={optionId}
                          type="button"
                          onClick={() => handleSucursalChange(optionId)}
                          className="w-full rounded-lg px-3 py-2 text-left hover:bg-[rgba(var(--panel),1)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="truncate text-sm text-[rgb(var(--fg))]">
                              {item?.codigo ? `${item.codigo} · ` : ''}{item?.nombre || `Sucursal ${optionId}`}
                            </span>
                            {isCurrent ? (
                              <span className="shrink-0 rounded-full bg-[rgba(var(--brand),0.14)] px-2 py-0.5 text-[10px] font-semibold text-[rgb(var(--brand-700))]">
                                Actual
                              </span>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          <hr className="my-2 border-[rgb(var(--border))]" />
          <button
            type="button"
            onClick={() => {
              setShowSucursalPicker(false);
              setShowPasswordForm(false);
              setOpen(false);
              navigate('/configuracion/impresion');
            }}
            className="w-full rounded-lg px-3 py-2 text-left hover:bg-[rgba(var(--subpanel),1)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Preferencias de impresión
          </button>

          <hr className="my-2 border-[rgb(var(--border))]" />
          <button
            type="button"
            onClick={() => {
              setShowSucursalPicker(false);
              setShowPasswordForm((value) => !value);
              setPasswordFeedback(null);
            }}
            className="w-full rounded-lg px-3 py-2 text-left hover:bg-[rgba(var(--subpanel),1)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Cambiar clave
          </button>

          {showPasswordForm ? (
            <form onSubmit={handlePasswordSubmit} className="mt-2 grid gap-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.7)] p-3">
              <label className="grid gap-1 text-xs font-medium text-[rgb(var(--fg))]">
                Clave actual
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(event) => handlePasswordChange('currentPassword', event.target.value)}
                  className="w-full rounded-lg border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  autoComplete="current-password"
                />
              </label>
              <label className="grid gap-1 text-xs font-medium text-[rgb(var(--fg))]">
                Nueva clave
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(event) => handlePasswordChange('newPassword', event.target.value)}
                  className="w-full rounded-lg border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  autoComplete="new-password"
                />
              </label>
              <label className="grid gap-1 text-xs font-medium text-[rgb(var(--fg))]">
                Confirmar clave
                <input
                  type="password"
                  value={passwordForm.repeatPassword}
                  onChange={(event) => handlePasswordChange('repeatPassword', event.target.value)}
                  className="w-full rounded-lg border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  autoComplete="new-password"
                />
              </label>
              {passwordFeedback ? (
                <div className={passwordFeedback.type === 'error' ? 'text-xs text-red-600' : 'text-xs text-emerald-600'}>
                  {passwordFeedback.message}
                </div>
              ) : null}
              <button
                type="submit"
                disabled={passwordSaving}
                className="rounded-lg bg-[rgb(var(--brand))] px-3 py-2 text-sm font-semibold text-white hover:bg-[rgb(var(--brand-700))] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {passwordSaving ? 'Guardando...' : 'Guardar clave'}
              </button>
            </form>
          ) : null}

          <hr className="my-2 border-[rgb(var(--border))]" />
          <button
            type="button"
            onClick={() => {
              setShowSucursalPicker(false);
              setShowPasswordForm(false);
              setOpen(false);
              logout();
              navigate('/login', { replace: true });
            }}
            className="w-full rounded-lg px-3 py-2 text-left text-red-600 hover:bg-[rgba(var(--subpanel),1)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Cerrar sesión
          </button>
        </div>
      ) : null}
    </div>
  );
}
